import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isTopTier, hasRole } from "@/lib/permissions";

export default function ProtectedRoute({ children, roles, check }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) {
    return (
      <div className="flex items-center justify-center h-screen text-slate-500 font-mono text-sm" data-testid="auth-loading">
        AUTHENTICATING...
      </div>
    );
  }
  if (!user) {
    // Route unauthenticated triage users to the triage login screen, not
    // the main ops console login. Everyone else lands at /login.
    const loginTarget = loc.pathname.startsWith("/triage") ? "/triage/login" : "/login";
    return <Navigate to={loginTarget} replace state={{ from: loc }} />;
  }
  // Force first-time password change before reaching any other protected page.
  if (user.must_change_password && loc.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (roles && roles.length > 0) {
    // Top-tier roles (super_admin / commanding_officer / staff_chief) always
    // pass. Otherwise the user must have the requested role either as their
    // primary or in additional_roles. Note that `admin` does NOT auto-pass
    // supervisor gates; admin only passes routes that explicitly list "admin".
    const extra = Array.isArray(user.additional_roles) ? user.additional_roles : [];
    const owned = { has: (r) => hasRole(user, r) };
    const allowed = isTopTier(user) || roles.some((r) => owned.has(r));
    if (!allowed) return <Navigate to="/" replace />;
  }
  // Custom per-route check (e.g. Validation Tool: only commanding_officer /
  // super_admin / original admin).
  if (typeof check === "function" && !check(user)) {
    return <Navigate to="/" replace />;
  }
  return children;
}
